import { useState } from "react";
import { isQuestionAnswered } from "../utils/questionProgress";
import { scaleOptions, shortScaleName } from "../utils/participantAssessmentType";

export function AssessmentRatingGuide({ T, defaultOpen = false, assessmentType }) {
  const [open, setOpen] = useState(defaultOpen);
  const dark = T.dark;
  const importanceOptions = scaleOptions(assessmentType, "importance");
  const agreementAll = scaleOptions(assessmentType, "agreement");
  const agreementOptions = agreementAll.filter(
    (o) => String(o).trim().toLowerCase() !== "unable to assess"
  );
  const unableLabel =
    agreementAll.find((o) => String(o).trim().toLowerCase() === "unable to assess") ||
    "Unable to Assess";
  const importanceName = shortScaleName(assessmentType, "importance");
  const agreementName = shortScaleName(assessmentType, "agreement");

  return (
    <div style={{ position: "relative", zIndex: 25, flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "5px 20px",
          border: "none",
          cursor: "pointer",
          background: T.guideToggleBg,
          borderBottom: `1px solid ${
            dark ? "rgba(255,255,255,0.05)" : "rgba(0,79,115,0.07)"
          }`,
        }}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke={
            dark ? "rgba(56,189,248,0.55)" : "rgba(0,79,115,0.45)"
          }
          strokeWidth={2.5}
        >
          <circle cx="12" cy="12" r="10" />
          <path strokeLinecap="round" d="M12 16v-4M12 8h.01" />
        </svg>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: dark ? "rgba(56,189,248,0.55)" : "rgba(0,79,115,0.45)",
          }}
        >
          Rating Scale Guide
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke={
            dark ? "rgba(56,189,248,0.40)" : "rgba(0,79,115,0.35)"
          }
          strokeWidth={2.5}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open ? (
        <div
          className="lga-rating-guide-cards"
          style={{
            background: T.guideBg,
            animation: "lga-fadein 0.15s ease both",
            borderBottom: `1px solid ${
              dark ? "rgba(255,255,255,0.05)" : "rgba(0,79,115,0.07)"
            }`,
          }}
        >
          <div
            className="lga-rating-guide-card"
            style={{
              borderRadius: 10,
              border: `1px solid ${
                dark ? "rgba(255,255,255,0.08)" : "rgba(0,79,115,0.10)"
              }`,
              background: dark ? "rgba(255,255,255,0.03)" : "#fff",
              padding: "10px 14px",
            }}
          >
            <p
              style={{
                margin: "0 0 3px",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: T.brand,
              }}
            >
              {importanceName}
            </p>
            <p
              style={{
                margin: "0 0 9px",
                fontSize: 10,
                color: T.text3,
                lineHeight: 1.4,
              }}
            >
              How critical is this to the company&apos;s success?
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {importanceOptions.map(
                (o) => (
                  <span
                    key={o}
                    style={{
                      padding: "3px 9px",
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 600,
                      background: dark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,79,115,0.05)",
                      border: `1px solid ${
                        dark
                          ? "rgba(255,255,255,0.10)"
                          : "rgba(0,79,115,0.13)"
                      }`,
                      color: dark ? "rgba(255,255,255,0.65)" : "#334155",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o}
                  </span>
                )
              )}
            </div>
          </div>

          <div
            className="lga-rating-guide-card"
            style={{
              borderRadius: 10,
              border: `1px solid ${
                dark ? "rgba(255,255,255,0.08)" : "rgba(0,79,115,0.10)"
              }`,
              background: dark ? "rgba(255,255,255,0.03)" : "#fff",
              padding: "10px 14px",
            }}
          >
            <p
              style={{
                margin: "0 0 3px",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: T.brand,
              }}
            >
              {agreementName}
            </p>
            <p
              style={{
                margin: "0 0 9px",
                fontSize: 10,
                color: T.text3,
                lineHeight: 1.4,
              }}
            >
              How well is the organization currently performing relative to this
              statement?
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {agreementOptions.map((o) => (
                <span
                  key={o}
                  style={{
                    padding: "3px 9px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 600,
                    background: dark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,79,115,0.05)",
                    border: `1px solid ${
                      dark
                        ? "rgba(255,255,255,0.10)"
                        : "rgba(0,79,115,0.13)"
                    }`,
                    color: dark ? "rgba(255,255,255,0.65)" : "#334155",
                    whiteSpace: "nowrap",
                  }}
                >
                  {o}
                </span>
              ))}
            </div>
            <div
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: `1px solid ${
                  dark ? "rgba(255,255,255,0.06)" : "rgba(0,79,115,0.08)"
                }`,
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  padding: "3px 9px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 600,
                  border: `1px dashed ${
                    dark ? "rgba(255,255,255,0.13)" : "rgba(0,79,115,0.16)"
                  }`,
                  color: T.text2,
                  whiteSpace: "nowrap",
                }}
              >
                {String(unableLabel).trim().toLowerCase() === "unable to assess"
                  ? "Unable to Assess"
                  : unableLabel}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: T.text3,
                  lineHeight: 1.45,
                  paddingTop: 2,
                }}
              >
                Select if you do not have sufficient knowledge or observable
                data to make an assessment.
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function AssessmentPillarStatus({
  T,
  steps = [],
  answers = {},
  activeStep = null,
}) {
  if (!steps?.length) return null;
  const dark = T.dark;
  const activeKey =
    activeStep != null && activeStep !== "" ? Number(activeStep) : null;

  return (
    <div
      style={{
        position: "relative",
        zIndex: 28,
        flexShrink: 0,
        padding: "7px 20px 6px",
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        justifyContent: "center",
        background: T.stripBg,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderBottom: `1px solid ${
          dark ? "rgba(255,255,255,0.05)" : "rgba(0,79,115,0.07)"
        }`,
      }}
    >
      {steps.map((s) => {
        const qs = s.questions || [];
        const done = qs.filter((q) => isQuestionAnswered(q, answers)).length;
        const total = qs.length;
        const pct = total ? Math.round((done / total) * 100) : 0;
        const complete = done === total && total > 0;
        const isActive =
          activeKey != null
            ? Number(s.step) === activeKey
            : false;
        const showCount = isActive;
        return (
          <div
            key={s.step}
            title={`${s.title}: ${done}/${total} answered`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px 3px 8px",
              borderRadius: 999,
              background: isActive
                ? dark
                  ? "rgba(56,189,248,0.14)"
                  : "rgba(0,79,115,0.10)"
                : complete
                  ? T.completeBg
                  : T.incompleteBg,
              border: `1px solid ${
                isActive
                  ? dark
                    ? "rgba(56,189,248,0.45)"
                    : "rgba(0,79,115,0.35)"
                  : complete
                    ? T.completeBorder
                    : T.incompleteBorder
              }`,
              boxShadow: isActive ? T.dotActive.shadow : undefined,
              transition: "all 0.3s",
            }}
          >
            {complete ? (
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke={T.completeColor}
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  display: "inline-block",
                  background: `conic-gradient(${T.meterFill} ${
                    pct * 3.6
                  }deg, ${T.meterEmpty} 0deg)`,
                }}
              />
            )}
            <span
              style={{
                fontSize: 10,
                fontWeight: isActive ? 800 : 600,
                letterSpacing: "0.03em",
                color: isActive
                  ? T.pillarColor
                  : complete
                    ? T.completeColor
                    : T.incompleteColor,
              }}
            >
              {String(s.title || "").split(" ")[0]}
            </span>
            {showCount ? (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: isActive ? 700 : 400,
                  color: isActive
                    ? T.pillarColor
                    : complete
                      ? dark
                        ? "rgba(74,222,128,0.65)"
                        : "rgba(22,163,74,0.60)"
                      : dark
                        ? "rgba(255,255,255,0.25)"
                        : "rgba(0,79,115,0.33)",
                }}
              >
                {done}/{total}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function AssessmentQuestionNav({
  T,
  questions = [],
  answers = {},
  activeQuestionId = null,
  onSelectQuestion = null,
}) {
  if (!questions?.length) return null;
  const dark = T.dark;

  return (
    <div
      style={{
        position: "relative",
        zIndex: 26,
        flexShrink: 0,
        padding: "6px 20px 5px",
        display: "flex",
        flexWrap: "wrap",
        gap: 5,
        justifyContent: "center",
        background: T.stripBgSoft,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderBottom: `1px solid ${
          dark ? "rgba(255,255,255,0.04)" : "rgba(0,79,115,0.06)"
        }`,
      }}
    >
      {questions.map((q, i) => {
        const answered = isQuestionAnswered(q, answers);
        const isActive =
          activeQuestionId != null &&
          String(q.id) === String(activeQuestionId);
        const dt = isActive
          ? T.dotActive
          : answered
            ? T.dotAnswered
            : T.dotEmpty;
        const label = String(q.id ?? i + 1);
        const clickable = typeof onSelectQuestion === "function";
        const Tag = clickable ? "button" : "div";
        return (
          <Tag
            key={String(q.id)}
            type={clickable ? "button" : undefined}
            title={label}
            onClick={clickable ? () => onSelectQuestion(q) : undefined}
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: dt.bg,
              border: `1px solid ${dt.border}`,
              color: dt.color,
              fontSize: 10,
              fontWeight: 700,
              boxShadow: isActive ? dt.shadow : undefined,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.25s",
              cursor: clickable ? "pointer" : "default",
              padding: 0,
            }}
          >
            {answered && !isActive ? (
              <svg
                width="9"
                height="9"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : label.length <= 4 ? (
              label
            ) : (
              i + 1
            )}
          </Tag>
        );
      })}
    </div>
  );
}
